import {PanelColorSettings} from '@wordpress/block-editor';
import type {SetAttributes} from '../pages/Edit';
import {StyleOptions} from '../WidgetStyles';
import {CheckboxControl, ColorIndicator, Panel, PanelBody, SelectControl} from '@wordpress/components';
import {CONFIG} from '../../../globals/config';
import {__} from '@wordpress/i18n';
import {useEffect} from 'react';
import {isScreen} from '../../../globals/helpers';
import FontSize from './FontSize';
import {useOpenSection} from '../../../hooks/useOpenSection';

import styles from './section.pcss';

type Props = {
	attributes: StyleOptions;
	setAttributes: SetAttributes;
};

const bulletStyles = [ {
	label: __( '- default -', 'advanced-sidebar-menu' ),
	value: '',
} ];
Object.entries( CONFIG.styles.bullets ).forEach( ( [ value, label ] ) => {
	bulletStyles.push( {value, label} );
} );

const Settings = ( {attributes, setAttributes}: Props ) => {
	const [ isOpen, toggleOpen ] = useOpenSection( attributes, setAttributes, 'settings' );

	useEffect( () => {
		/**
		 * On the post edit screen, having apply current parent
		 * styles is redundant since the menu does not change.
		 * Instead, we set the values to true, so the styles will
		 * work and hide the inputs.
		 */
		if ( 'post' === CONFIG.currentScreen ) {
			setAttributes( {
				apply_current_page_parent_styles_to_parent: true,
				apply_current_page_styles_to_parent: true,
			} );
		}
	}, [ setAttributes ] );

	return (
		<Panel className={styles.settings}>
			<PanelBody
				title={<span className={styles.title}>
					{__( 'Settings', 'advanced-sidebar-menu' )}
					{! isOpen &&
						true === attributes.block_style &&
						true === attributes.border &&
						<span className={styles.indicators}>
							<ColorIndicator
								className={styles.swatch}
								colorValue={attributes.border_color}
							/>
						</span>}
				</span>}
				opened={isOpen}
				onToggle={toggleOpen}
			>
				<span className={styles.spacer} />
				{! isScreen( [ 'post' ] ) && <CheckboxControl
					label={__( 'Apply current item styles to the highest level parent item (when applicable)', 'advanced-sidebar-menu' )}
					checked={true === attributes.apply_current_page_styles_to_parent}
					onChange={value => {
						setAttributes( {
							apply_current_page_styles_to_parent: value,
						} );
					}}
					// @ts-expect-error -- Not technically available until WP 6.7.
					__nextHasNoMarginBottom
				/>}
				{! isScreen( [ 'post' ] ) && <CheckboxControl
					label={__( 'Apply current item parent styles to the highest level parent item (when applicable)', 'advanced-sidebar-menu' )}
					checked={true === attributes.apply_current_page_parent_styles_to_parent}
					onChange={value => {
						setAttributes( {
							apply_current_page_parent_styles_to_parent: value,
						} );
					}}
					// @ts-expect-error -- Not technically available until WP 6.7.
					__nextHasNoMarginBottom
				/>}
				<SelectControl
					label={__( 'Bullet style', 'advanced-sidebar-menu' )}
					labelPosition={'side'}
					value={attributes.bullet_style ?? ''}
					options={bulletStyles}
					onChange={value => {
						setAttributes( {
							bullet_style: value,
						} );
					}} />
				<CheckboxControl
					label={__( 'Use blocked styling', 'advanced-sidebar-menu' )}
					checked={true === attributes.block_style}
					onChange={value => {
						setAttributes( {
							block_style: value,
						} );
					}}
					// @ts-expect-error -- Not technically available until WP 6.7.
					__nextHasNoMarginBottom
				/>
				{true === attributes.block_style && <>
					<CheckboxControl
						label={__( 'Show borders', 'advanced-sidebar-menu' )}
						checked={true === attributes.border}
						onChange={value => {
							setAttributes( {
								border: value,
							} );
						}}
						// @ts-expect-error -- Not technically available until WP 6.7.
						__nextHasNoMarginBottom
					/>
					{true === attributes.border && <>
						<PanelColorSettings
							className={styles.borderColor}
							colorSettings={[ {
								value: attributes.border_color ?? '',
								onChange: color => setAttributes( {
									border_color: color,
								} ),
								label: __( 'Border color', 'advanced-sidebar-menu' ),
							} ]}
						/>
						<FontSize
							label={__( 'Border width', 'advanced-sidebar-menu' )}
							value={( attributes.border_width ?? '' ).toString()}
							onChange={value => {
								setAttributes( {
									border_width: value,
								} );
							}} />
					</>}
				</>}
				<span className={styles.spacer} />
			</PanelBody>
		</Panel>
	);
};

export default Settings;
